// src/features/rrhh/horarios/components/ShiftDetailModal.jsx

import { useCallback, useEffect, useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  MenuItem,
  Typography,
  Box,
  IconButton,
  Divider,
  CircularProgress,
  Popover,
  Checkbox,
  Chip,
  Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import StorefrontIcon from "@mui/icons-material/Storefront";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import BadgeIcon from "@mui/icons-material/Badge";
import EditIcon from "@mui/icons-material/Edit";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ExtensionIcon from '@mui/icons-material/Extension'

/* ═════════ Helpers ═════════ */

const DIAS_SEMANA = [
  "",
  "Lunes",
  "Martes",
  "Miércoles",
  "Jueves",
  "Viernes",
  "Sábado",
  "Domingo",
];

const MESES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

function fmtDateLabel(ymd) {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  const dow = dt.getDay(); 
  const dowMap = [7, 1, 2, 3, 4, 5, 6]; 
  const iso = dowMap[dow];
  return `${DIAS_SEMANA[iso]}, ${String(d).padStart(2, "0")} ${MESES[m - 1]}`;
}

function hhmm(v) {
  if (!v) return "";
  return String(v).slice(0, 5);
}

function timeToMin(hm) {
  const s = String(hm || "").slice(0, 5);
  if (!/^\d{2}:\d{2}$/.test(s)) return null;
  const [h, m] = s.split(":").map(Number);
  return h * 60 + m;
}

function endsNextDay(startTime, endTime) {
  const s = timeToMin(startTime);
  const e = timeToMin(endTime);
  if (s == null || e == null) return false;
  return e < s;
}

function minToHHMM(value) {
  const total = ((Math.round(Number(value) || 0) % 1440) + 1440) % 1440;
  const h = String(Math.floor(total / 60)).padStart(2, "0");
  const m = String(total % 60).padStart(2, "0");
  return `${h}:${m}`;
}

function scheduleBounds(startTime, endTime) {
  const startBase = timeToMin(startTime);
  const endBase = timeToMin(endTime);
  if (startBase == null || endBase == null || startBase === endBase) return null;

  return {
    startAbs: startBase,
    endAbs: endBase <= startBase ? endBase + 24 * 60 : endBase,
  };
}

function normalizeTurnoIntervals(turnos = [], scheduleStartTime = "", scheduleEndTime = "") {
  const bounds = scheduleBounds(scheduleStartTime, scheduleEndTime);
  let previousEnd = null;

  const normalized = turnos.map((turno, originalIndex) => {
    const startBase = timeToMin(turno.startTime);
    const endBase = timeToMin(turno.endTime);

    if (startBase == null || endBase == null) {
      return { ...turno, originalIndex, startAbs: null, endAbs: null };
    }

    let startDayOffset = 0;
    let startAbs = startBase + startDayOffset * 24 * 60;

    if (bounds) {
      while (startAbs < bounds.startAbs) {
        startDayOffset += 1;
        startAbs += 24 * 60;
      }
    } else {
      while (previousEnd != null && startAbs < previousEnd) {
        startDayOffset += 1;
        startAbs += 24 * 60;
      }
    }

    let endDayOffset = startDayOffset;
    let endAbs = endBase + endDayOffset * 24 * 60;

    if (endAbs <= startAbs) {
      endDayOffset += 1;
      endAbs += 24 * 60;
    }

    if (!bounds) {
      previousEnd = endAbs;
    }

    return {
      ...turno,
      originalIndex,
      startDayOffset,
      endDayOffset,
      startAbs,
      endAbs,
    };
  });

  return normalized.sort((a, b) => (a.startAbs ?? 0) - (b.startAbs ?? 0));
}

function deriveScheduleFromTurnos(turnos = []) {
  const normalized = normalizeTurnoIntervals(turnos);
  const first = normalized[0];
  const last = normalized[normalized.length - 1];

  return {
    startTime: first?.startTime ? hhmm(first.startTime) : "09:00",
    endTime: last?.endTime ? hhmm(last.endTime) : "17:00",
  };
}

function turnoUsesNextDay(turno) {
  return Number(turno?.startDayOffset || 0) > 0 || Number(turno?.endDayOffset || 0) > 0;
}

function renderPlaceholderValue(value, items = [], placeholder = "Seleccionar...") {
  if (!value) {
    return (
      <Typography
        component="span"
        sx={{
          fontSize: 14,
          fontStyle: "italic",
          color: "text.disabled",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis"
        }}
      >
        {placeholder}
      </Typography>
    );
  }

  const found = items.find((it) => String(it.id) === String(value));

  return (
    <Typography
      component="span"
      sx={{
        fontSize: 14,
        color: "primary.900",
        fontWeight: 600,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis"
      }}
    >
      {found?.name || value}
    </Typography>
  );
}

function findSectorById(sectors, id) {
  if (!id) return null;
  return (sectors || []).find((sector) => String(sector.id) === String(id)) || null;
}

function findSectorByName(sectors, name) {
  const text = String(name || "").trim().toLocaleLowerCase();
  if (!text) return null;
  return (sectors || []).find((sector) => String(sector.name || "").trim().toLocaleLowerCase() === text) || null;
}

function sectorDisplayName(turno, sectors) {
  const selected = findSectorById(sectors, turno.sectorId);
  return selected?.name || turno.sectorName || "";
}

function roleDisplayName(turno, roles) {
  const selected = (roles || []).find((role) => String(role.id) === String(turno.roleId));
  return selected?.name || turno.roleName || "";
}

function DetailRow({ icon, label, children, noDivider, alignTop }) {
  return (
    <>
      <Stack
        direction="row"
        alignItems={alignTop ? "flex-start" : "center"}
        spacing={1.5}
        sx={{ py: 1.2, px: 0.5 }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 32,
            color: "neutral.600",
            pt: alignTop ? 0.5 : 0,
          }}
        >
          {icon}
        </Box>

        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 700,
            color: "primary.900",
            textTransform: "uppercase",
            minWidth: 80,
            letterSpacing: "0.04em",
            pt: alignTop ? 0.5 : 0,
          }}
        >
          {label}
        </Typography>

        <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end", overflow: "hidden" }}>
          {children}
        </Box>
      </Stack>

      {!noDivider && <Divider />}
    </>
  );
}

function fmtShortDateLabel(ymd) {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  const dow = dt.getDay();
  const dowMap = [7, 1, 2, 3, 4, 5, 6];
  const iso = dowMap[dow];
  return `${DIAS_SEMANA[iso]} ${d}`;
}

/* ═════════ Component ═════════ */

export default function ShiftDetailModal({
  open,
  onClose,
  mode: initialMode = "view",
  shiftData = null,
  initialCreateTurnos = null,
  workDate = "",
  employeeId,
  weekKey,
  weekDates = [],
  employeeWeekShifts = [],
  branches = [],
  areas = [],
  roles = [],
  sections = [],
  defaultBranchId = "",
  defaultAreaId = "",
  defaultRoleId = "",
  defaultSectionId = "",
  defaultSectorName = "",
  onSave,
  onDelete,
  saving = false,
}) {
  const [mode, setMode] = useState(initialMode);
  const [turnos, setTurnos] = useState([]);
  const [scheduleStartTime, setScheduleStartTime] = useState("09:00");
  const [scheduleEndTime, setScheduleEndTime] = useState("17:00");
  const [scheduleManuallyEdited, setScheduleManuallyEdited] = useState(false);
  const [error, setError] = useState("");
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [selectedWorkDates, setSelectedWorkDates] = useState([]);
  const [dateAnchorEl, setDateAnchorEl] = useState(null);

  const maxTurnos = Number.POSITIVE_INFINITY;
  const isEditable = mode === "edit" || mode === "create";
  const dateLabel = fmtDateLabel(workDate);
  const normalizedTurnosPreview = useMemo(
    () => normalizeTurnoIntervals(turnos, scheduleStartTime, scheduleEndTime),
    [turnos, scheduleStartTime, scheduleEndTime],
  );

  const normalizeInitialTurno = useCallback((turno) => {
    const resolvedSectorId = turno.sectorId ?? turno.sector_id ?? turno.sectionId ?? "";
    const resolvedSectorName =
      turno.sectorName ??
      turno.sector_name ??
      turno.sectionName ??
      turno.section_name ??
      turno.section ??
      "";
    
    return {
      id: turno.id ?? null,
      turnId: turno.turnId ?? turno.turn_id ?? turno.id ?? null,
      scheduleId: turno.scheduleId ?? turno.schedule_id ?? null,
      startTime: hhmm(turno.startTime),
      endTime: hhmm(turno.endTime),
      startDayOffset: Number(turno.startDayOffset ?? turno.start_day_offset ?? 0),
      endDayOffset: Number(turno.endDayOffset ?? turno.end_day_offset ?? 0),
      branchId: turno.branchId ?? "",
      areaId: turno.areaId ?? "",
      roleId: turno.roleId ?? turno.role_id ?? defaultRoleId ?? "",
      roleName: turno.roleName ?? turno.role_name ?? turno.role?.name ?? "",
      sectorId: resolvedSectorId,
      sectorName: resolvedSectorId ? "" : resolvedSectorName,
    };
  }, [defaultRoleId]);

  useEffect(() => {
    if (!open) return;

    if (initialMode === "create" || !shiftData) {
      setMode("create");
        
      let nextTurnos;
      if (Array.isArray(initialCreateTurnos) && initialCreateTurnos.length > 0) {
        nextTurnos = initialCreateTurnos.map(normalizeInitialTurno);
      } else {
        nextTurnos = [
          {
            id: null,
            startTime: "09:00",
            endTime: "17:00",
            branchId: defaultBranchId || "",
            areaId: defaultAreaId || "",
            roleId: defaultRoleId || "",
            sectorId: defaultSectionId || "",
            sectorName: defaultSectorName || "",
          },
        ];
      }

      const derived = deriveScheduleFromTurnos(nextTurnos);
      setTurnos(nextTurnos);
      setScheduleStartTime(derived.startTime);
      setScheduleEndTime(derived.endTime);
      setScheduleManuallyEdited(false);
    } else {
      setMode("view");
      const sourceTurnos = Array.isArray(shiftData.turns) && shiftData.turns.length
        ? shiftData.turns
        : [shiftData];
      const nextTurnos = sourceTurnos.map(normalizeInitialTurno);

      setTurnos(nextTurnos);
      setScheduleStartTime(hhmm(shiftData.scheduleStartTime || shiftData.startTime) || deriveScheduleFromTurnos(nextTurnos).startTime);
      setScheduleEndTime(hhmm(shiftData.scheduleEndTime || shiftData.endTime) || deriveScheduleFromTurnos(nextTurnos).endTime);
      setScheduleManuallyEdited(true);
    }

    setSelectedWorkDates(workDate ? [workDate] : []);
    setDateAnchorEl(null);
    setError("");
  }, [
    open,
    shiftData,
    initialMode,
    initialCreateTurnos,
    defaultBranchId,
    defaultAreaId,
    defaultRoleId,
    defaultSectionId,
    defaultSectorName,
    workDate,
    normalizeInitialTurno,
  ]);

  useEffect(() => {
    if (!isEditable || scheduleManuallyEdited || !turnos.length) return;

    const derived = deriveScheduleFromTurnos(turnos);
    setScheduleStartTime(derived.startTime);
    setScheduleEndTime(derived.endTime);
  }, [isEditable, scheduleManuallyEdited, turnos]);

  const shiftsCountByDate = useMemo(() => {
    const map = new Map();
    for (const sh of employeeWeekShifts || []) {
      const ymd = sh?.workDate || sh?.work_date;
      if (!ymd) continue;
      map.set(ymd, (map.get(ymd) || 0) + 1);
    }
    return map;
  }, [employeeWeekShifts]);

  const weekDateOptions = useMemo(() => {
    return (weekDates || []).map((date) => {
      const isBaseDate = date === workDate;
      const existingCount = shiftsCountByDate.get(date) || 0;

      return {
        value: date,
        label: fmtShortDateLabel(date),
        isBaseDate,
        disabled: !isBaseDate && existingCount > 0,
        existingCount,
        checked: selectedWorkDates.includes(date),
      };
    });
  }, [weekDates, workDate, shiftsCountByDate, selectedWorkDates]);

  const selectedDatesLabel = useMemo(() => {
    if (!selectedWorkDates.length) return dateLabel || "—";
    if (selectedWorkDates.length === 1) return fmtDateLabel(selectedWorkDates[0]);
    return `${selectedWorkDates.length} días seleccionados`;
  }, [selectedWorkDates, dateLabel]);

  function toggleWorkDate(date) {
    if (!date || date === workDate) return;

    setSelectedWorkDates((prev) => {
      const exists = prev.includes(date);

      if (exists) {
        const next = prev.filter((d) => d !== date);
        return next.length ? next : [workDate];
      }

      const order = new Map((weekDates || []).map((d, i) => [d, i]));
      return [...prev, date].sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0));
    });
  }

  function updateScheduleStart(value) {
    setError("");
    setScheduleManuallyEdited(true);
    setScheduleStartTime(value);
    setTurnos((prev) => {
      if (!prev.length) return prev;
      return prev.map((turno, index) => (index === 0 ? { ...turno, startTime: value } : turno));
    });
  }

  function updateScheduleEnd(value) {
    setError("");
    setScheduleManuallyEdited(true);
    setScheduleEndTime(value);
    setTurnos((prev) => {
      if (!prev.length) return prev;
      return prev.map((turno, index) =>
        index === prev.length - 1 ? { ...turno, endTime: value } : turno,
      );
    });
  }

  function addTurno() {
    if (turnos.length >= maxTurnos) return;

    setTurnos((prev) => {
      if (!prev.length) {
        return [
          {
            id: null,
            startTime: scheduleStartTime || "09:00",
            endTime: scheduleEndTime || "17:00",
            branchId: defaultBranchId || "",
            areaId: defaultAreaId || "",
            roleId: defaultRoleId || "",
            sectorId: defaultSectionId || "",
            sectorName: defaultSectorName || "",
          },
        ];
      }

      const normalized = normalizeTurnoIntervals(prev, scheduleStartTime, scheduleEndTime);
      const lastNormalized = normalized[normalized.length - 1];
      const lastIndex = lastNormalized?.originalIndex ?? prev.length - 1;
      const last = prev[lastIndex];
      const duration = Math.max(0, (lastNormalized?.endAbs ?? 0) - (lastNormalized?.startAbs ?? 0));
      const schedule = scheduleBounds(scheduleStartTime, scheduleEndTime);

      if (duration >= 60 && schedule && lastNormalized.endAbs === schedule.endAbs) {
        const midpoint = lastNormalized.startAbs + Math.round(duration / 2 / 15) * 15;
        const midpointTime = minToHHMM(midpoint);
        const previousEnd = last.endTime;

        return [
          ...prev.slice(0, lastIndex),
          { ...last, endTime: midpointTime },
          {
            id: null,
            startTime: midpointTime,
            endTime: previousEnd,
            branchId: last?.branchId || defaultBranchId || "",
            areaId: last?.areaId || defaultAreaId || "",
            roleId: last?.roleId || defaultRoleId || "",
            sectorId: last?.sectorId || defaultSectionId || "",
            sectorName: last?.sectorName || defaultSectorName || "",
          },
          ...prev.slice(lastIndex + 1),
        ];
      }

      const defaultStart = last?.endTime || scheduleStartTime || "13:00";
      const startMin = timeToMin(defaultStart);
      const fallbackEnd = startMin != null ? startMin + 240 : timeToMin("21:00");
      const defaultEnd = scheduleEndTime || minToHHMM(fallbackEnd);

      return [
        ...prev,
        {
          id: null,
          startTime: defaultStart,
          endTime: defaultEnd,
          branchId: last?.branchId || defaultBranchId || "",
          areaId: last?.areaId || defaultAreaId || "",
          roleId: last?.roleId || defaultRoleId || "",
          sectorId: last?.sectorId || defaultSectionId || "",
          sectorName: last?.sectorName || defaultSectorName || "",
        },
      ];
    });
  }

  function removeTurno(idx) {
    if (turnos.length <= 1) return;
    setTurnos((prev) => prev.filter((_, i) => i !== idx));
  }

  function updateTurno(idx, patch) {
    setError("");
    setTurnos((prev) => prev.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
  }

  function validate() {
    if (turnos.length === 0) return "Debe haber al menos 1 turno.";

    if (!scheduleStartTime || !scheduleEndTime) {
      return "Debe completar el horario.";
    }

    const schedule = scheduleBounds(scheduleStartTime, scheduleEndTime);
    if (!schedule) {
      return "La hora fin del horario no puede ser igual a la hora inicio.";
    }

    for (let i = 0; i < turnos.length; i++) {
      const t = turnos[i];

      if (!t.startTime || !t.endTime) {
        return `Turno ${i + 1}: debe completar hora de inicio y fin.`;
      }
      
      if (!t.branchId) return `Turno ${i + 1}: Debe seleccionar una sucursal.`;
      if (!t.areaId) return `Turno ${i + 1}: Debe seleccionar un área.`;
      if (!t.roleId) return `Turno ${i + 1}: Debe seleccionar un rol.`;

      const s = timeToMin(t.startTime);
      const e = timeToMin(t.endTime);

      if (s == null || e == null) {
        return `Turno ${i + 1}: formato de hora inválido.`;
      }

      if (s === e) {
        return `Turno ${i + 1}: la hora fin no puede ser igual a la hora inicio.`;
      }
    }

    const normalized = normalizeTurnoIntervals(turnos, scheduleStartTime, scheduleEndTime);

    if (normalized[0]?.startAbs !== schedule.startAbs) {
      return "El primer turno debe empezar junto con el horario.";
    }

    if (normalized[normalized.length - 1]?.endAbs !== schedule.endAbs) {
      return "El último turno debe terminar junto con el horario.";
    }

    for (let i = 1; i < normalized.length; i++) {
      if (normalized[i].startAbs < normalized[i - 1].endAbs) {
        return "Los turnos no pueden superponerse.";
      }
    }

    for (let i = 0; i < normalized.length; i++) {
      if (normalized[i].startAbs < schedule.startAbs || normalized[i].endAbs > schedule.endAbs) {
        return "Los turnos no pueden quedar fuera del horario.";
      }
    }

    if (mode === "create" && (!selectedWorkDates.length || !selectedWorkDates.includes(workDate))) {
      return "Debe haber al menos un día seleccionado.";
    }

    return "";
  }

  function handleSave() {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError("");

    const normalizedTurnos = normalizeTurnoIntervals(turnos, scheduleStartTime, scheduleEndTime);

    const payload = {
      scheduleId: shiftData?.scheduleId ?? shiftData?.schedule_id ?? null,
      scheduleStartTime: `${hhmm(scheduleStartTime)}:00`,
      scheduleEndTime: `${hhmm(scheduleEndTime)}:00`,
      turnos: normalizedTurnos.map((t) => ({
        id: t.turnId ?? t.id ?? null,
        turnId: t.turnId ?? t.id ?? null,
        startTime: `${hhmm(t.startTime)}:00`,
        endTime: `${hhmm(t.endTime)}:00`,
        startDayOffset: Number(t.startDayOffset || 0),
        endDayOffset: Number(t.endDayOffset || 0),
        branchId: Number(t.branchId),
        areaId: Number(t.areaId),
        roleId: Number(t.roleId),
        sectorId: t.sectorId ? Number(t.sectorId) : null,
        sectorName: String(t.sectorName || "").trim() || null,
        section: sectorDisplayName(t, sections) || null,
        sectionId: t.sectorId ? String(t.sectorId) : "",
      })),
      employeeId,
      workDate,
      workDates: mode === "create" ? selectedWorkDates : [workDate],
      weekKey,
    };

    onSave?.(payload, mode, shiftData?.id ?? null);
  }

  const editableInputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      height: 38,
      fontSize: 14,
      px: 1, 
    },
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px",
            overflow: "hidden",
            boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 0.5,
            pt: 2,
            px: 2.5,
          }}
        >
          <Typography
            sx={{
              fontSize: 20,
              fontWeight: 700,
              color: "primary.800",
            }}
          >
            Detalle de Jornada
          </Typography>

          <Stack direction="row" spacing={0.5}>
            {mode === "view" && shiftData?.id && (
              <IconButton
                size="small"
                onClick={() => setConfirmDeleteOpen(true)}
                title="Eliminar jornada"
                sx={{ color: "error.main" }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            )}

            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ px: 2.5, py: 1.5 }}>
          <DetailRow icon={<CalendarTodayIcon fontSize="small" />} label="Fecha">
            {mode === "create" ? (
              <>
                <Button
                  variant="outlined"
                  onClick={(e) => setDateAnchorEl(e.currentTarget)}
                  endIcon={<KeyboardArrowDownIcon />}
                  sx={{
                    textTransform: "none",
                    borderRadius: "10px",
                    minWidth: 210,
                    justifyContent: "space-between",
                    px: 1.4,
                    color: "primary.900",
                    borderColor: "neutral.300",
                    fontWeight: 500,
                  }}
                >
                  {selectedDatesLabel}
                </Button>
                
                <Popover
                  open={Boolean(dateAnchorEl)}
                  anchorEl={dateAnchorEl}
                  onClose={() => setDateAnchorEl(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  PaperProps={{
                    sx: { mt: 1, borderRadius: "14px", p: 1, minWidth: 230 },
                  }}
                >
                  <Stack spacing={0.4}>
                    {weekDateOptions.map((day) => {
                      const checked = selectedWorkDates.includes(day.value);
                      return (
                        <Button
                          key={day.value}
                          variant="text"
                          onClick={() => {
                            if (!day.disabled && !day.isBaseDate) toggleWorkDate(day.value);
                          }}
                          disabled={day.disabled}
                          sx={{
                            textTransform: "none",
                            justifyContent: "space-between",
                            px: 1,
                            py: 0.7,
                            borderRadius: "10px",
                            color: "primary.900",
                            opacity: day.disabled ? 0.5 : 1,
                          }}
                        >
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Checkbox
                              size="small"
                              checked={checked}
                              disabled={day.disabled || day.isBaseDate}
                              sx={{ p: 0.2 }}
                            />
                            <Typography sx={{ fontSize: 13, fontWeight: day.isBaseDate ? 700 : 500 }}>
                              {day.label}
                            </Typography>
                          </Stack>
                        
                          {day.isBaseDate ? (
                            <Chip label="Actual" size="small" sx={{ height: 22, fontSize: 11 }} />
                          ) : day.disabled ? (
                            <Chip label="Con horario" size="small" sx={{ height: 22, fontSize: 11 }} />
                          ) : null}
                        </Button>
                      );
                    })}
                  </Stack>
                </Popover>
              </>
            ) : (
              <Typography sx={{ fontSize: 14, fontWeight: 500, color: "primary.900" }}>
                {dateLabel || "—"}
              </Typography>
            )}
          </DetailRow>

          <DetailRow
            icon={<AccessTimeIcon fontSize="small" />}
            label="Horario"
            alignTop={isEditable}
          >
            {isEditable ? (
              <Stack spacing={2} sx={{ width: "100%", mt: 0.5, alignItems: "flex-end" }}>
                <Box sx={{ p: 1.5, bgcolor: "neutral.50", borderRadius: "12px", border: "1px solid", borderColor: "neutral.200" }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Stack spacing={0.4}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: "neutral.600" }}>Entrada</Typography>
                      <TextField
                        type="time"
                        size="small"
                        value={scheduleStartTime}
                        onChange={(e) => updateScheduleStart(e.target.value)}
                        inputProps={{ step: 300 }}
                        sx={{ ...editableInputSx, width: 150 }}
                      />
                    </Stack>
                    <Typography sx={{ fontSize: 13, color: "neutral.600", pt: 2 }}>—</Typography>
                    <Stack spacing={0.4} alignItems="flex-start">
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: "neutral.600" }}>Salida</Typography>
                      <TextField
                        type="time"
                        size="small"
                        value={scheduleEndTime}
                        onChange={(e) => updateScheduleEnd(e.target.value)}
                        inputProps={{ step: 300 }}
                        sx={{ ...editableInputSx, width: 150 }}
                      />
                    </Stack>
                    {endsNextDay(scheduleStartTime, scheduleEndTime) && (
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: "secondary.700", pt: 2 }}>
                        (Día sig.)
                      </Typography>
                    )}
                  </Stack>
                </Box>
              </Stack>
            ) : (
              <Box sx={{ textAlign: "right", bgcolor: "neutral.50", p: 1.5, borderRadius: "12px", border: "1px solid", borderColor: "neutral.200", width: "fit-content" }}>
                <Typography sx={{ fontSize: 16, fontWeight: 800, color: "primary.900" }}>
                  {scheduleStartTime || "--:--"} – {scheduleEndTime || "--:--"} {endsNextDay(scheduleStartTime, scheduleEndTime) ? <span style={{ color: "#d32f2f", fontSize: 12 }}>(Día sig.)</span> : ""}
                </Typography>
              </Box>
            )}
          </DetailRow>

          <DetailRow
            icon={<AccessTimeIcon fontSize="small" />}
            label="Turnos"
            alignTop
            noDivider
          >
            {isEditable ? (
              <Stack spacing={2} sx={{ width: "100%", mt: 0.5 }}>
                {turnos.map((t, idx) => {
                  const preview = normalizedTurnosPreview.find((item) => item.originalIndex === idx) || t;
                  return (
                  <Box key={idx} sx={{ p: 1.5, bgcolor: "neutral.50", borderRadius: "12px", border: "1px solid", borderColor: "neutral.200", position: "relative" }}>
                    
                    {turnos.length > 1 && (
                      <IconButton
                        size="small"
                        onClick={() => removeTurno(idx)}
                        sx={{ position: "absolute", top: 4, right: 4, color: "error.main", p: 0.3 }}
                      >
                        <RemoveCircleOutlineIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    )}

                    <Stack spacing={1.5} sx={{ pr: turnos.length > 1 ? 3 : 0 }}>
                      {/* Times */}
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Stack spacing={0.4}>
                          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "neutral.600" }}>Entrada</Typography>
                          <TextField
                            type="time"
                            size="small"
                            value={t.startTime}
                            onChange={(e) => updateTurno(idx, { startTime: e.target.value })}
                            inputProps={{ step: 300 }}
                            // 🔥 Aumentado de 105 a 150 🔥
                            sx={{ ...editableInputSx, width: 150 }} 
                          />
                        </Stack>
                        <Typography sx={{ fontSize: 13, color: "neutral.600", pt: 2 }}>—</Typography>
                        <Stack spacing={0.4} alignItems="flex-start">
                          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "neutral.600" }}>Salida</Typography>
                          <TextField
                            type="time"
                            size="small"
                            value={t.endTime}
                            onChange={(e) => updateTurno(idx, { endTime: e.target.value })}
                            inputProps={{ step: 300 }}
                            // 🔥 Aumentado de 105 a 150 🔥
                            sx={{ ...editableInputSx, width: 150 }} 
                          />
                        </Stack>
                        {(endsNextDay(t.startTime, t.endTime) || turnoUsesNextDay(preview)) && (
                          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "secondary.700", pt: 2 }}>
                            (Día sig.)
                          </Typography>
                        )}
                      </Stack>

                      {/* Selects de Sucursal / Area / Sector para este turno con íconos integrados */}
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                        <TextField
                          select
                          size="small"
                          value={t.branchId}
                          onChange={(e) => updateTurno(idx, { branchId: e.target.value })}
                          sx={{ ...editableInputSx, flex: 1 }}
                          InputProps={{
                            startAdornment: <StorefrontIcon sx={{ color: "primary.700", fontSize: 18, mr: 0.5 }} />
                          }}
                          SelectProps={{
                            displayEmpty: true,
                            renderValue: (value) => renderPlaceholderValue(value, branches, "Sucursal"),
                          }}
                        >
                          <MenuItem value="" disabled><em>Sucursal</em></MenuItem>
                          {branches.map((b) => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
                        </TextField>

                        <TextField
                          select
                          size="small"
                          value={t.areaId}
                          onChange={(e) => updateTurno(idx, { areaId: e.target.value })}
                          sx={{ ...editableInputSx, flex: 1 }}
                          InputProps={{
                            startAdornment: <WorkOutlineIcon sx={{ color: "primary.700", fontSize: 18, mr: 0.5 }} />
                          }}
                          SelectProps={{
                            displayEmpty: true,
                            renderValue: (value) => renderPlaceholderValue(value, areas, "Área"),
                          }}
                        >
                          <MenuItem value="" disabled><em>Área</em></MenuItem>
                          {areas.map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
                        </TextField>

                        {sections.length ? (
                          <Autocomplete
                            freeSolo
                            autoSelect
                            size="small"
                            options={sections}
                            value={findSectorById(sections, t.sectorId) || t.sectorName || ""}
                            getOptionLabel={(option) =>
                              typeof option === "string" ? option : option?.name || ""
                            }
                            isOptionEqualToValue={(option, value) =>
                              String(option?.id) === String(value?.id)
                            }
                            onChange={(_, value) => {
                              if (!value) {
                                updateTurno(idx, { sectorId: "", sectorName: "" });
                                return;
                              }

                              if (typeof value === "string") {
                                const matched = findSectorByName(sections, value);
                                updateTurno(
                                  idx,
                                  matched
                                    ? { sectorId: matched.id, sectorName: "" }
                                    : { sectorId: "", sectorName: value.trim() },
                                );
                                return;
                              }

                              updateTurno(idx, { sectorId: value.id, sectorName: "" });
                            }}
                            onInputChange={(_, value, reason) => {
                              if (reason !== "input") return;
                              const matched = findSectorByName(sections, value);
                              updateTurno(
                                idx,
                                matched
                                  ? { sectorId: matched.id, sectorName: "" }
                                  : { sectorId: "", sectorName: value.trim() },
                              );
                            }}
                            sx={{ flex: 1 }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                placeholder="Sector"
                                sx={editableInputSx}
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <>
                                      <ExtensionIcon sx={{ color: "primary.700", fontSize: 18, mr: 0.5 }} />
                                      {params.InputProps.startAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                          />
                        ) : (
                          <TextField
                            size="small"
                            value={t.sectorName || ""}
                            onChange={(e) => updateTurno(idx, { sectorId: "", sectorName: e.target.value })}
                            placeholder="Sector"
                            sx={{ ...editableInputSx, flex: 1 }}
                            InputProps={{
                              startAdornment: <ExtensionIcon sx={{ color: "primary.700", fontSize: 18, mr: 0.5 }} />
                            }}
                          />
                        )}

                        <TextField
                          select
                          size="small"
                          value={t.roleId}
                          onChange={(e) => updateTurno(idx, { roleId: e.target.value })}
                          sx={{ ...editableInputSx, flex: 1 }}
                          InputProps={{
                            startAdornment: <BadgeIcon sx={{ color: "primary.700", fontSize: 18, mr: 0.5 }} />
                          }}
                          SelectProps={{
                            displayEmpty: true,
                            renderValue: (value) => renderPlaceholderValue(value, roles, "Rol"),
                          }}
                        >
                          <MenuItem value="" disabled><em>Rol</em></MenuItem>
                          {roles.map((role) => <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>)}
                        </TextField>
                      </Stack>
                    </Stack>
                  </Box>
                  );
                })}

                {turnos.length < maxTurnos && (
                  <Button
                    size="small"
                    startIcon={<AddCircleOutlineIcon sx={{ fontSize: 16 }} />}
                    onClick={addTurno}
                    sx={{ textTransform: "none", fontSize: 13, fontWeight: 600, color: "secondary.600", alignSelf: "flex-start" }}
                  >
                    Dividir turno
                  </Button>
                )}
              </Stack>
            ) : (
              <Stack spacing={2} sx={{ width: "100%", alignItems: "flex-end" }}>
                {turnos.map((t, idx) => {
                  const preview = normalizedTurnosPreview.find((item) => item.originalIndex === idx) || t;
                  const bName = branches.find(b => Number(b.id) === Number(t.branchId))?.name || "—";
                  const aName = areas.find(a => Number(a.id) === Number(t.areaId))?.name || "—";
                  const sName = sectorDisplayName(t, sections) || "—";
                  const rName = roleDisplayName(t, roles) || "—";
                  return (
                    <Box key={idx} sx={{ textAlign: "right", bgcolor: "neutral.50", p: 1.5, borderRadius: "12px", border: "1px solid", borderColor: "neutral.200" }}>
                      <Typography sx={{ fontSize: 15, fontWeight: 800, color: "primary.900", mb: 0.5 }}>
                        {t.startTime || "--:--"} – {t.endTime || "--:--"} {(endsNextDay(t.startTime, t.endTime) || turnoUsesNextDay(preview)) ? <span style={{ color: "#d32f2f", fontSize: 12 }}>(Día sig.)</span> : ""}
                      </Typography>
                      
                      {/* Etiquetas restauradas con sus íconos en Modo View */}
                      <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ mt: 1 }}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <StorefrontIcon sx={{ fontSize: 14, color: "neutral.500" }} />
                          <Typography sx={{ fontSize: 12, color: "neutral.700", fontWeight: 600 }}>{bName}</Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <WorkOutlineIcon sx={{ fontSize: 14, color: "neutral.500" }} />
                          <Typography sx={{ fontSize: 12, color: "neutral.700", fontWeight: 600 }}>{aName}</Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <ExtensionIcon sx={{ fontSize: 14, color: "neutral.500" }} />
                          <Typography sx={{ fontSize: 12, color: "neutral.700", fontWeight: 600 }}>{sName}</Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <BadgeIcon sx={{ fontSize: 14, color: "neutral.500" }} />
                          <Typography sx={{ fontSize: 12, color: "neutral.700", fontWeight: 600 }}>{rName}</Typography>
                        </Stack>
                      </Stack>

                    </Box>
                  );
                })}
              </Stack>
            )}
          </DetailRow>

          {error && (
            <Typography sx={{ mt: 1.5, fontSize: 12, fontWeight: 700, color: "error.main", textAlign: "center" }}>
              {error}
            </Typography>
          )}
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 2.5, py: 1.5, justifyContent: "center", gap: 1.5 }}>
          {mode === "view" && (
            <>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => setMode("edit")}
                sx={{ textTransform: "none", borderRadius: "10px", fontWeight: 600, px: 3 }}
              >
                Editar jornada
              </Button>

              <Button
                variant="outlined"
                onClick={onClose}
                sx={{ textTransform: "none", borderRadius: "10px", fontWeight: 600, px: 3 }}
              >
                Cerrar
              </Button>
            </>
          )}

          {(mode === "edit" || mode === "create") && (
            <>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                sx={{ textTransform: "none", borderRadius: "10px", fontWeight: 600, px: 3, minWidth: 120 }}
              >
                {saving ? <CircularProgress size={20} color="inherit" /> : mode === "create" ? "Crear jornada" : "Guardar"}
              </Button>

              <Button
                variant="outlined"
                onClick={() => {
                  if (mode === "create") {
                    onClose?.();
                    return;
                  }

                  setMode("view");
                  if (shiftData) {
                    const sourceTurnos = Array.isArray(shiftData.turns) && shiftData.turns.length
                      ? shiftData.turns
                      : [shiftData];
                    const nextTurnos = sourceTurnos.map(normalizeInitialTurno);
                    setTurnos(nextTurnos);
                    setScheduleStartTime(hhmm(shiftData.scheduleStartTime || shiftData.startTime) || deriveScheduleFromTurnos(nextTurnos).startTime);
                    setScheduleEndTime(hhmm(shiftData.scheduleEndTime || shiftData.endTime) || deriveScheduleFromTurnos(nextTurnos).endTime);
                    setScheduleManuallyEdited(true);
                  }
                  setError("");
                }}
                sx={{ textTransform: "none", borderRadius: "10px", fontWeight: 600, px: 3 }}
              >
                Cancelar
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        PaperProps={{ sx: { borderRadius: "16px", px: 1, py: 0.5 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <Typography>¿Estás seguro que deseas borrar el horario?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDeleteOpen(false)} sx={{ textTransform: "none", borderRadius: "10px", fontWeight: 600 }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              setConfirmDeleteOpen(false);
              onDelete?.(shiftData.scheduleId ?? shiftData.schedule_id ?? shiftData.id);
            }}
            sx={{ textTransform: "none", borderRadius: "10px", fontWeight: 600 }}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
